import type {CSSProperties, ReactElement} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {ITEM_ICON, ITEM_LABEL_KEY} from '../../layers-panel.types';
import {useItemThumbDataURL} from '@Hooks/poster-editor/useItemThumbDataURL';
import styles from './layer-row.module.scss';
import panelStyles from '../../layers-panel.module.scss';

interface LayerRowProps {
  uid: string;
  isSelected: boolean;
  isInsideGroup?: boolean;
  isOverlay?: boolean;
  disableDrag?: boolean;
  onClick?: (uid: string) => void;
}

export function LayerRow({uid, isSelected, isInsideGroup = false, isOverlay = false, disableDrag = false, onClick}: LayerRowProps): ReactElement {
  const {attributes, isDragging, listeners, setNodeRef, transform, transition} = useSortable({id: uid, disabled: disableDrag});
  const thumbSrc = useItemThumbDataURL(uid);

  const showDropPlaceholder = isDragging && !isOverlay;
  const hideOverlayClone = isDragging && isOverlay;

  const style: CSSProperties = {
    ...(hideOverlayClone && {opacity: 0}),
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const onClickRow = (): void => {
    onClick?.(uid);
  };

  const rowClasses = `${styles.row} flexbox flex-items-center gap-2 spacing-p-2 ${isSelected ? styles.selected : ''} ${isInsideGroup ? 'spacing-m-l-5' : ''} ${showDropPlaceholder ? panelStyles.dragging : ''}`;

  const renderPreview = (): ReactElement => {
    if (thumbSrc) {
      return <img alt="" className={styles.thumb} draggable={false} src={thumbSrc} />;
    }
    return <Icon icon={ITEM_ICON} size={IconSize.SIZE_ICON_16} className={`${styles.icon} flex-shrink-0`} />;
  };

  const renderDropPlaceholder = (): ReactElement | null => {
    if (!showDropPlaceholder) {
      return null;
    }
    return <div className={panelStyles.dropPlaceholder} />;
  };

  const renderDragHandle = (): ReactElement => {
    return <Icon icon="icon-draggable" size={IconSize.SIZE_ICON_16} className={`${styles.dragHandle} flex-shrink-0`} />;
  };

  return (
    <ClickableDiv
      ref={setNodeRef}
      className={rowClasses}
      style={style}
      enableFocusOutline={false}
      onClick={onClickRow}
      aria-label={window.i18next.t(ITEM_LABEL_KEY)}
      {...attributes}
      {...listeners}
    >
      {renderDropPlaceholder()}
      <div className={`${styles.handleSlot} flex-center flex-shrink-0`}>{renderDragHandle()}</div>
      <div className={`${styles.thumbSlot} flex-center flex-1`}>{renderPreview()}</div>
      <div className={`${styles.trailingSlot} flex-center flex-shrink-0`}>{isSelected ? <Icon icon="icon-check" size={IconSize.SIZE_ICON_16} className={`${styles.checkmark} flex-shrink-0`} /> : null}</div>
    </ClickableDiv>
  );
}
